<template>
  <el-dialog
    class="template-preview"
    v-model="visible"
    :title="t('designer.templatePreview.title')"
    align-center
    :close-on-click-modal="false"
  >
    <template #header>
      <div style="width: 100%; text-align: center; font-size: 24px; font-weight: bold">
        {{ t('designer.templatePreview.title') }}
      </div>
    </template>

    <div v-loading="spinning">
      <div id="preview_content_design" ref="previewContent"></div>
    </div>

    <template #footer>
      <div style="width: 100%; display: flex; justify-content: space-between">
        <el-button @click="hideModal"> {{ t('designer.templatePreview.close') }} </el-button>
        <div>
          <el-button :loading="waitShowPrinter" @click.stop="print">
            <el-icon><Printer /></el-icon>
            {{ t('designer.templatePreview.print') }}
          </el-button>
          <el-button :loading="exportLoading" @click.stop="toPdf">
            <el-icon><Document /></el-icon>
            {{ t('designer.templatePreview.pdf') }}
          </el-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, nextTick, watch } from 'vue'
import { Printer, Document } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { usePrintAction } from './composables/usePrintAction.js'
import { useErpI18n } from '@/composables/useErpI18n'

// 使用多语言
const { t } = useErpI18n()

// 定义 hiprint 相关类型
interface PrintInstance {
  getHtml(data: any): string
  print(data: any, options?: any, config?: { callback?: () => void }): void
  toPdf(data: any, title?: string): void
}

interface ShowOptions {
  templateName?: string
}

// 响应式状态
const visible = ref<boolean>(false)
const spinning = ref<boolean>(true)
const waitShowPrinter = ref<boolean>(false)
const printInstance = ref<PrintInstance | null>(null)
const templateName = ref<string>('')
const printData = ref<any>({})
const previewContent = ref<HTMLDivElement | null>(null)
const exportLoading = ref<boolean>(false)

/**
 * 隐藏模态框
 */
const hideModal = (): void => {
  visible.value = false
}

/**
 * 显示打印预览
 * @param newPrintInstance - hiprint 模板对象
 * @param newPrintData - 打印数据
 * @param options - 选项配置
 */
const show = (
  newPrintInstance: PrintInstance,
  newPrintData: any,
  options: ShowOptions = {},
): void => {
  try {
    if (!newPrintInstance) {
      ElMessage.error(t('designer.templatePreview.templateEmpty'))
      return
    }

    visible.value = true
    spinning.value = true
    templateName.value = options.templateName || '导出模板.pdf'
    printInstance.value = newPrintInstance
    printData.value = newPrintData

    nextTick(() => {
      try {
        if (previewContent.value && printInstance.value) {
          const jqueryResult: any = printInstance.value.getHtml(printData.value)
          // 清空容器
          previewContent.value.innerHTML = ''
          // 如果是 jQuery 对象，获取其 HTML 内容
          if (jqueryResult && typeof jqueryResult === 'object' && jqueryResult.length) {
            // 创建临时容器来获取 HTML
            const tempDiv = document.createElement('div')
            tempDiv.appendChild(jqueryResult[0].cloneNode(true))
            previewContent.value.innerHTML = tempDiv.innerHTML
          } else if (typeof jqueryResult === 'string') {
            // 如果是字符串，直接设置
            previewContent.value.innerHTML = jqueryResult
          }
        }
      } catch (error) {
        console.error('渲染预览内容失败:', error)
        ElMessage.error(t('designer.templatePreview.renderFailed'))
      } finally {
        spinning.value = false
      }
    })
  } catch (error) {
    console.error('显示预览失败:', error)
    ElMessage.error(t('designer.templatePreview.showPreviewFailed'))
    spinning.value = false
  }
}

const { printAction, exportPDF } = usePrintAction()

const print = () => {
  if (waitShowPrinter.value || exportLoading.value) {
    return
  }
  waitShowPrinter.value = true
  printAction({
    printInstance: ref(printInstance),
    printData,
    waitShowPrinter,
  })
}

const toPdf = (): void => {
  if (waitShowPrinter.value || exportLoading.value) {
    return
  }
  exportLoading.value = true
  exportPDF({
    printInstance: ref(printInstance),
    printData: ref(printData),
    download: true,
    type: 'blob',
    fileName: templateName.value,
    callback: (status: string, res: any) => {
      if (status === 'exportSuccess') {
        ElMessage.success(t('designer.templatePreview.exportSuccess'))
      } else if (status === 'exportError') {
        ElMessage.error(`${t('designer.templatePreview.exportFailed')}: ${res}`)
      }
      exportLoading.value = false
    },
  })
}

// 暴露组件方法供父组件调用
defineExpose({
  show,
})

// 弹窗关闭时 销毁printInstance ，重置pritData
watch(visible, (newVal) => {
  if (!newVal) {
    printInstance.value = null
    printData.value = {}
  }
})
</script>

<style lang="scss" scoped>
.template-preview {
  height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  margin: auto;
  .el-dialog__header {
    flex-shrink: 0;
  }

  .el-dialog__footer {
    flex-shrink: 0;
  }
  .el-dialog__body {
    flex: 1;
    padding: 20px;
    overflow-y: auto;
    max-height: calc(80vh - 120px); // 减去头部和底部的高度
    #preview_content_design {
      // min-height: 400px;
      max-height: calc(80vh - 120px);
      overflow-y: auto;
      width: 100%;
      border: 1px solid #e4e7ed;
      border-radius: 4px;
      background: white;
      padding: 20px;
      box-sizing: border-box;
    }
  }
}
</style>
